// Short-term loudness (EBU R128 / ITU-R BS.1770-4): K-weighted mean square over the last 3 seconds, no gating.
// The AudioWorklet filters every channel and reports 100 ms blocks; ShortTermLoudness combines the last 30 of them.

const PROCESSOR_NAME = "ks-lufs-meter";

// K-weighting filter coefficients for any sample rate (same formulas as libebur128)
const PROCESSOR_CODE = `
function k_weighting(rate) {
  var f0 = 1681.974450955533;
  var G = 3.999843853973347;
  var Q = 0.7071752369554196;
  var K = Math.tan(Math.PI * f0 / rate);
  var Vh = Math.pow(10, G / 20);
  var Vb = Math.pow(Vh, 0.4996667741545416);
  var a0 = 1 + K / Q + K * K;
  var pre = {
    b0: (Vh + Vb * K / Q + K * K) / a0,
    b1: 2 * (K * K - Vh) / a0,
    b2: (Vh - Vb * K / Q + K * K) / a0,
    a1: 2 * (K * K - 1) / a0,
    a2: (1 - K / Q + K * K) / a0
  };
  f0 = 38.13547087602444;
  Q = 0.5003270373238773;
  K = Math.tan(Math.PI * f0 / rate);
  var d = 1 + K / Q + K * K;
  var rlb = {a1: 2 * (K * K - 1) / d, a2: (1 - K / Q + K * K) / d};
  return {pre: pre, rlb: rlb};
}

class LufsMeterProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.k = k_weighting(sampleRate);
    this.block = Math.round(sampleRate / 10);
    this.count = 0;
    this.sums = [];
    this.states = [];
  }
  process(inputs) {
    var input = inputs[0] || [];
    var n = input.length;
    if (n === 0) return true;
    if (this.sums.length !== n) {
      this.sums = new Array(n).fill(0);
      this.states = [];
      for (var s = 0; s < n; s++) this.states.push(new Float64Array(6));
      this.count = 0;
    }
    var pre = this.k.pre;
    var rlb = this.k.rlb;
    var len = input[0].length;
    for (var c = 0; c < n; c++) {
      var x = input[c];
      var st = this.states[c];
      // The high-pass stage takes the pre-filter output, so they share its history (y1, y2)
      var x1 = st[0], x2 = st[1], y1 = st[2], y2 = st[3], z1 = st[4], z2 = st[5];
      var sum = 0;
      for (var i = 0; i < len; i++) {
        var xi = x[i];
        var y = pre.b0 * xi + pre.b1 * x1 + pre.b2 * x2 - pre.a1 * y1 - pre.a2 * y2;
        var z = y - 2 * y1 + y2 - rlb.a1 * z1 - rlb.a2 * z2;
        x2 = x1; x1 = xi;
        y2 = y1; y1 = y;
        z2 = z1; z1 = z;
        sum += z * z;
      }
      st[0] = x1; st[1] = x2; st[2] = y1; st[3] = y2; st[4] = z1; st[5] = z2;
      this.sums[c] += sum;
    }
    this.count += len;
    if (this.count >= this.block) {
      this.port.postMessage({sums: this.sums, count: this.count});
      this.sums = new Array(n).fill(0);
      this.count = 0;
    }
    return true;
  }
}

registerProcessor("${PROCESSOR_NAME}", LufsMeterProcessor);
`;

const loaded = new WeakMap();

export function lufsSupported() {
	return !!((window.AudioContext || window.webkitAudioContext) && window.AudioWorkletNode);
}

// Creates the meter node for the context; the module is loaded once per context
export function createLufsMeter(ctx) {
	var promise = loaded.get(ctx);
	if (!promise) {
		var url = URL.createObjectURL(new Blob([PROCESSOR_CODE], {type: "application/javascript"}));
		promise = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
		loaded.set(ctx, promise);
	}
	return promise.then(() => new AudioWorkletNode(ctx, PROCESSOR_NAME, {numberOfOutputs: 1, outputChannelCount: [1]}));
}

// BS.1770 channel weights; 5.1 is L R C LFE Ls Rs (LFE excluded)
function channel_weights(n) {
	if (n === 6) return [1, 1, 1, 0, 1.41, 1.41];
	if (n === 5) return [1, 1, 1, 1.41, 1.41];
	return new Array(n).fill(1);
}

export class ShortTermLoudness {
	constructor () {
		this.blocks = [];
	}
	// sums: K-weighted sum of squares per channel over `count` samples
	add (sums, count) {
		if (this.blocks.length && this.blocks[0].sums.length !== sums.length) this.blocks = [];
		this.blocks.push({sums: sums, count: count});
		if (this.blocks.length > 30) this.blocks.shift();
	}
	// Short-term LUFS (3 s), -Infinity for silence; blocks = 4 gives the momentary loudness (400 ms)
	value (blocks) {
		if (!this.blocks.length) return -Infinity;
		var weights = channel_weights(this.blocks[0].sums.length);
		var total = 0;
		var count = 0;
		this.blocks.slice(-(blocks || 30)).forEach(b => {
			count += b.count;
			b.sums.forEach((s, c) => { total += weights[c] * s; });
		});
		var ms = count ? total / count : 0;
		return ms > 0 ? -0.691 + 10 * Math.log10(ms) : -Infinity;
	}
}
